import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './TraderCashReserveSettings.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const RESERVE_PRESETS = [0, 5, 10, 20, 50, 100];

function presetFromValue(value) {
  const n = Number(value) || 0;
  if (RESERVE_PRESETS.includes(n)) return { preset: String(n), custom: '' };
  return { preset: 'custom', custom: String(n) };
}

const COPY = {
  hub: {
    title: 'Untouchable wallet reserve',
    description: (
      <>
        USDC on your account held back from MAP trade sizing — applies to both{' '}
        <strong>Manual Trades</strong> and <strong>oBot</strong>. Default $0 (fully deployable). Suggested
        margin and automated MAP orders scale to the same book % as the master Omni portfolio, using your
        equity minus this reserve. On manual trades you still choose leverage and confirm each order.
      </>
    ),
    saveMessage: (usd) =>
      `Cash reserve saved: $${usd} USDC — applies to Manual Trades and oBot.`,
  },
};

export default function TraderCashReserveSettings({
  compact = false,
  className = '',
  variant = 'hub',
}) {
  const [preset, setPreset] = useState('0');
  const [customUsd, setCustomUsd] = useState('');
  const [savedUsd, setSavedUsd] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/multi-asset/status`, { headers: authHeaders() });
      const reserve = Number(data.traderReserveUsd) || 0;
      setSavedUsd(reserve);
      const { preset: p, custom } = presetFromValue(reserve);
      setPreset(p);
      setCustomUsd(custom);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolveAmount = () =>
    preset === 'custom' ? Math.max(0, parseFloat(customUsd) || 0) : parseFloat(preset) || 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const traderReserveUsd = resolveAmount();
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/user/settings`,
        { traderReserveUsd },
        { headers: authHeaders() }
      );
      setSavedUsd(data.traderReserveUsd);
      const copy = COPY[variant] || COPY.hub;
      setMessage(copy.saveMessage(data.traderReserveUsd));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copy = COPY[variant] || COPY.hub;

  return (
    <section className={`trader-cash-reserve ${compact ? 'compact' : ''} ${className}`.trim()}>
      <h3>{copy.title}</h3>
      <p className="trader-cash-reserve-desc">{copy.description}</p>
      {error && <div className="error-message trader-cash-reserve-error">{error}</div>}
      {message && <div className="trader-cash-reserve-success">{message}</div>}
      <div className="trader-cash-reserve-row">
        <label>
          Reserve amount
          <select value={preset} onChange={(e) => setPreset(e.target.value)}>
            {RESERVE_PRESETS.map((v) => (
              <option key={v} value={String(v)}>
                ${v}{v === 0 ? ' (default — fully deployable)' : ''}
              </option>
            ))}
            <option value="custom">Custom amount…</option>
          </select>
        </label>
        {preset === 'custom' && (
          <label>
            Custom (USD)
            <input
              type="number"
              min={0}
              step={1}
              value={customUsd}
              onChange={(e) => setCustomUsd(e.target.value)}
              placeholder="e.g. 250"
            />
          </label>
        )}
        <button type="button" className="secondary-btn" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save reserve'}
        </button>
      </div>
      {!message && savedUsd > 0 && (
        <p className="trader-cash-reserve-current">Currently saved: ${savedUsd} USDC reserve.</p>
      )}
    </section>
  );
}
